import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import { Add, CreateOutlined, VisibilityOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { bankInfoValidationSchema } from "../form-validations";
import axios from "axios";
import customAxios from "../../../../api";
import "./bank-information.scss";
import { updateBankDetails } from "../../../../api/provider-section/provider-service-management";
import InfoPopup from "../../../../common/components/info-popup/info-popup";

const accountTypes = ["Checking", "Savings", "Business"];

interface BankInformationProps {
  data: any;
}

interface BankFormValues {
  bankTaxProfileEIN: string;
  bankAccountType: string;
  bankAccountHolderName: string;
  bankRoutingNumberABA: string;
  bankAccountNumber: string;
  w9Form: any;
}

const maskNumber = (value: string, visibleDigits = 4) => {
  if (!value) return "-";

  const lastDigits = value.slice(-visibleDigits);
  return lastDigits.padStart(value.length, "X");
};

const TruncatedTooltip: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowed(element.scrollWidth > element.offsetWidth);
    }
  }, [children]);

  return (
    <Tooltip title={title} disableHoverListener={!isOverflowed} arrow placement="top-start">
      <Typography ref={textRef} className={className}>
        {children}
      </Typography>
    </Tooltip>
  );
};

const BankInformation: React.FC<BankInformationProps> = ({ data }) => {
  const [editMode, setEditMode] = useState(false);
  const [infoPopup, setInfoPopup] = useState({
    open: false,
    title: "",
    note: "",
  });

  const initialValues: BankFormValues = {
    bankTaxProfileEIN: data?.bankTaxProfileEIN || "",
    bankAccountType: data?.bankAccountType || "",
    bankAccountHolderName: data?.bankAccountHolderName || "",
    bankRoutingNumberABA: data?.bankRoutingNumberABA || "",
    bankAccountNumber: data?.bankAccountNumber || "",
    w9Form: data?.w9Form || null,
  };

  const formik = useFormik<BankFormValues>({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: bankInfoValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      console.log("VALUES::", values);

      await handleUpdateBankDetails(values);
      setSubmitting(false);
      setEditMode(false);
    },
  });

  const allowDigitsOnly = (fieldName: keyof BankFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      formik.setFieldValue(fieldName, value);
    }
  };

  const allowAlphabetsOnly = (fieldName: keyof BankFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      formik.setFieldValue(fieldName, value);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files[0]) {
      formik.setFieldValue("w9Form", event.currentTarget.files[0]);
    }
  };

  const handleUpdateBankDetails = async (values: BankFormValues) => {
    try {
      const formData = new FormData();
      formData.append("taxProfileEIN", values.bankTaxProfileEIN);
      formData.append("accountType", values.bankAccountType);
      formData.append("accountHolderName", values.bankAccountHolderName);
      formData.append("routingNumber", values.bankRoutingNumberABA);
      formData.append("accountNumber", values.bankAccountNumber);

      if (values.w9Form && values.w9Form instanceof File) {
        formData.append("w9_form", values.w9Form);
      }

      console.log("formdata::", formData);

      const response = await updateBankDetails(formData);

      if (response.data.success) {
        setInfoPopup({
          open: true,
          title: "Success",
          note: "Bank details updated successfully!",
        });
        setEditMode(false);
      } else {
        setInfoPopup({
          open: true,
          title: "Error",
          note: "Failed to update bank details. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("Error updating bank details:", error);
      const errorData = error?.response?.data;
      setInfoPopup({
        open: true,
        title: "Error",
        note: errorData?.error === "FILE_TOO_LARGE"
          ? `${errorData.message}. ${errorData.details}`
          : errorData?.message || "An error occurred. Please try again.",
      });
    }
  };

  const handleDownload = async (url: string, fileName: string) => {
    if (!url) return;

    // If it's already a blob URL (e.g. locally selected file), download it directly
    if (url.startsWith("blob:")) {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    try {
      const isExternal =
        url.startsWith("http") && !url.includes(window.location.host);

      // Use raw axios for external links to avoid standard interceptors (like Auth headers)
      const axiosInstance = isExternal ? axios : customAxios;

      const response = await axiosInstance({
        url,
        method: "GET",
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName || "document");
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: If programmatic download fails, open in a new tab
      window.open(url, "_blank");
    }
  };

  const getFileName = (source: any) => {
    if (!source) return "";
    if (Array.isArray(source) && source.length > 0) {
      return source[0].fileName || "W9_Form";
    }
    if (typeof source === "string") {
      return source.split("/").pop()?.split("?")[0] || "W9_Form";
    }
    return source.name || "W9_Form";
  };

  const getFileUrl = (source: any) => {
    if (!source) return "";
    if (Array.isArray(source) && source.length > 0) {
      return source[0].url || "";
    }
    if (typeof source === "string") {
      return source;
    }
    if (source instanceof File) {
      return URL.createObjectURL(source);
    }
    return "";
  };

  const renderLabel = (label: string, required = false) => (
    <Typography
      sx={{ fontWeight: 500, mt: "1rem", mb: "0.5rem" }}
      className="bank-information__label"
    >
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
  );

  const renderField = (
    name: keyof BankFormValues,
    placeholder: string,
    digitsOnly = false,
    alphabetsOnly = false,
    maxLength = 30,
  ) => {
    if (editMode) {
      return (
        <TextField
          fullWidth
          id={name}
          name={name}
          placeholder={placeholder}
          value={formik.values[name]}
          onChange={
            digitsOnly
              ? allowDigitsOnly(name)
              : alphabetsOnly
                ? allowAlphabetsOnly(name)
                : formik.handleChange
          }
          onBlur={formik.handleBlur}
          error={formik.touched[name] && Boolean(formik.errors[name])}
          helperText={
            (formik.touched[name] && (formik.errors[name] as string)) || ""
          }
          className="bank-information__input"
          inputProps={{
            maxLength: maxLength,
          }}
        />
      );
    }

    const shouldMask =
      name === "bankAccountNumber" || name === "bankRoutingNumberABA";

    const displayValue = shouldMask
      ? maskNumber(formik.values[name] as string)
      : (formik.values[name] as string) || "-";

    return (
      <Typography className="bank-information__value">
        {displayValue}
      </Typography>
    );
  };

  return (
    <Box
      className="bank-information"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      {/* Header with Edit / Save Cancel */}
      <Box className="bank-information__header">
        {!editMode ? (
          <CreateOutlined
            onClick={() => setEditMode(true)}
            className="basic-information__edit-icon"
            fontSize="small"
          />
        ) : (
          <Box className="basic-information-form__actions">
            <Button
              variant="outlined"
              className="basic-information-form__cancel-btn"
              onClick={() => {
                formik.resetForm();
                setEditMode(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className="basic-information-form__save-btn">
              Save
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={2} className="bank-information__form">
        {/* Left Column */}
        <Grid item xs={12} md={6} className="bank-information__col">
          <Box display="flex" flexDirection="column">
            {renderLabel("Tax Profile/EIN", true)}
            {renderField("bankTaxProfileEIN", "Enter Tax Profile/EIN")}

            {renderLabel("Account Holder Name", true)}
            {renderField(
              "bankAccountHolderName",
              "Enter Account Holder Name",
              false,
              true,
            )}

            {renderLabel("Account Number", true)}
            {renderField("bankAccountNumber", "Enter Account Number", true)}
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6} className="bank-information__col">
          <Box display="flex" flexDirection="column">
            {renderLabel("Account Type", true)}
            {editMode ? (
              <TextField
                fullWidth
                select
                id="bankAccountType"
                name="bankAccountType"
                value={formik.values.bankAccountType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.bankAccountType &&
                  Boolean(formik.errors.bankAccountType)
                }
                helperText={
                  (formik.touched.bankAccountType &&
                    (formik.errors.bankAccountType as string)) || ""
                }
                className="bank-information__input"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected: any) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#9ca3af" }}>
                          Select Account Type
                        </span>
                      );
                    }
                    return selected as string;
                  },
                }}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography className="bank-information__value">
                {formik.values.bankAccountType || "-"}
              </Typography>
            )}

            {renderLabel("Routing Number (ABA)")}
            {renderField(
              "bankRoutingNumberABA",
              "Enter Routing Number (ABA)",
              true,
              false,
              10
            )}

            {renderLabel("W9 Form")}
            {editMode ? (
              <Tooltip title={formik.values.w9Form ? getFileName(formik.values.w9Form) : ""} arrow placement="top-start">
                <TextField
                  fullWidth
                  placeholder="No file chosen"
                  value={formik.values.w9Form ? getFileName(formik.values.w9Form) : ""}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          component="label"
                          className="bank-information__upload-btn-new"
                        >
                          Upload
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                          />
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                  className="bank-information__input bank-information__input--file"
                />
              </Tooltip>
            ) : formik.values.w9Form ? (
              <Box className="bank-information__file-wrapper">
                <TruncatedTooltip title={getFileName(formik.values.w9Form)} className="bank-information__file-name">
                  {getFileName(formik.values.w9Form)}
                </TruncatedTooltip>
                <Box className="bank-information__file-actions">
                  <VisibilityOutlined
                    className="bank-information__icon bank-information__icon--view"
                    onClick={() => {
                      const url = getFileUrl(formik.values.w9Form);
                      if (url) window.open(url, "_blank");
                    }}
                  />
                  <FileDownloadOutlined
                    className="bank-information__icon bank-information__icon--download"
                    onClick={() => {
                      const url = getFileUrl(formik.values.w9Form);
                      if (url) {
                        handleDownload(url, getFileName(formik.values.w9Form));
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Typography className="bank-information__value">-</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <InfoPopup
        open={infoPopup.open}
        title={infoPopup.title}
        note={infoPopup.note}
        onClose={() => setInfoPopup((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default BankInformation;
